import { CropCommand } from "../commands/CropCommand";
import type { Document } from "../document/Document";
import { screenToDoc } from "../render/Viewport";
import type { PointerSample, Tool, ToolContext } from "./Tool";

export interface CropToolOptions {
  readonly id: string;
  readonly shortcut: string;
}

export interface CropRect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  readonly rotation: number;
}

/**
 * Crop overlay style — drawn over the crop rect to help with composition.
 * Photoshop offers Rule of Thirds (default), Grid, Diagonal, Triangle,
 * Golden Ratio, Golden Spiral. (#15)
 */
export type CropOverlay =
  | "rule-thirds"
  | "grid"
  | "diagonal"
  | "triangle"
  | "golden-ratio"
  | "golden-spiral";

type CropHandle =
  | "corner-tl" | "corner-tr" | "corner-bl" | "corner-br"
  | "edge-t" | "edge-b" | "edge-l" | "edge-r"
  | "rotation"
  | "inside"
  | null;

interface CropDrag {
  handle: CropHandle;
  anchorX: number;
  anchorY: number;
  startX: number;
  startY: number;
  startW: number;
  startH: number;
  startRotation: number;
}

type StraightenPhase = "idle" | "drawing";

const HANDLE_TOL = 6;
const ROTATION_HANDLE_DIST = 18;

/** Snap an angle (radians) to the nearest 15° — used when Shift is held. */
function snapTo15Deg(radians: number): number {
  const step = (15 * Math.PI) / 180;
  return Math.round(radians / step) * step;
}

export class CropTool implements Tool {
  readonly id: string;
  readonly shortcut: string;

  /** When true, pixels outside the crop are deleted (Photoshop default). */
  deletePixels = false;

  /** Aspect ratio constraints (width/height). null = unconstrained. */
  aspectRatio: number | null = null;

  /** Crop overlay style (Rule of Thirds, Grid, Diagonal, …). #15 */
  overlay: CropOverlay = "rule-thirds";

  /**
   * Pixels-per-inch for the cropped image. Mirrors the Resolution field in
   * Photoshop's crop options bar.
   */
  resolution = 72;

  /**
   * When true (and rotation/expansion exposes empty pixels), the renderer
   * fills those areas using content-aware fill on commit.
   */
  contentAware = false;

  /**
   * When true, the canvas outside the crop rect renders at reduced opacity
   * (Photoshop's "Crop Shield"). Enabled by default to match PS.
   */
  shieldOutside = true;

  /** Shield opacity (0..1). Default matches Photoshop's 75% black overlay. */
  shieldOpacity = 0.75;

  private drag: CropDrag | null = null;
  private rect: CropRect | null = null;
  private straightenPhase: StraightenPhase = "idle";
  private straightenStartX = 0;
  private straightenStartY = 0;
  private straightenEndX = 0;
  private straightenEndY = 0;
  isStraightenActive = false;

  constructor(opts: CropToolOptions = { id: "crop", shortcut: "C" }) {
    this.id = opts.id;
    this.shortcut = opts.shortcut;
  }

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc) return;
    // The crop rect/shield is an overlay-only field (not part of the document
    // tree), so mutating it does not notify EditorState subscribers. Like the
    // other overlay-driven tools (PerspectiveCrop, ObjectSelection, Patch) we
    // must explicitly request a render so the crop marquee is actually drawn as
    // the pointer is pressed/dragged — without this the rect is created in
    // state but never painted, so drag-to-create appears to do nothing.
    ctx.requestRender();
    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const px = Math.floor(p.x);
    const py = Math.floor(p.y);

    // Straighten mode: draw a line to define the horizon.
    if (this.straightenPhase === "idle" && this.isStraightenActive) {
      this.straightenPhase = "drawing";
      this.straightenStartX = px;
      this.straightenStartY = py;
      this.straightenEndX = px;
      this.straightenEndY = py;
      return;
    }

    // If there's an existing crop rect, check for handle hit or inside drag.
    if (this.rect && this.rect.w > 0 && this.rect.h > 0) {
      const handle = this.hitTestHandle(this.rect, px, py);
      if (handle) {
        this.drag = {
          handle,
          anchorX: px,
          anchorY: py,
          startX: this.rect.x,
          startY: this.rect.y,
          startW: this.rect.w,
          startH: this.rect.h,
          startRotation: this.rect.rotation,
        };
        return;
      }
      if (this.isInsideRect(this.rect, px, py)) {
        this.drag = {
          handle: "inside",
          anchorX: px,
          anchorY: py,
          startX: this.rect.x,
          startY: this.rect.y,
          startW: this.rect.w,
          startH: this.rect.h,
          startRotation: this.rect.rotation,
        };
        return;
      }
    }

    // No existing rect or click outside: start a new crop rect.
    this.rect = null;
    this.drag = {
      handle: null,
      anchorX: px,
      anchorY: py,
      startX: px,
      startY: py,
      startW: 0,
      startH: 0,
      startRotation: 0,
    };
    this.rect = { x: px, y: py, w: 0, h: 0, rotation: 0 };
  }

  onPointerMove(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc) return;
    // Redraw the live crop marquee as it is dragged (see onPointerDown). The
    // render is async (rAF); the rect mutation below completes synchronously
    // first, so the scheduled frame paints the updated rect.
    ctx.requestRender();
    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const cx = Math.floor(p.x);
    const cy = Math.floor(p.y);

    // Straighten mode: update the end point of the line.
    if (this.straightenPhase === "drawing") {
      this.straightenEndX = cx;
      this.straightenEndY = cy;
      return;
    }

    if (!this.drag) return;
    const d = this.drag;
    const dx = cx - d.anchorX;
    const dy = cy - d.anchorY;

    if (d.handle === "inside" && this.rect) {
      // Reposition the crop rect.
      const nx = d.startX + dx;
      const ny = d.startY + dy;
      this.rect = { x: nx, y: ny, w: d.startW, h: d.startH, rotation: d.startRotation };
      return;
    }

    if (d.handle === "rotation" && this.rect) {
      // Rotation: compute angle from center of rect to cursor. Shift snaps
      // to the nearest 15° step (Photoshop convention).
      const centerX = this.rect.x + this.rect.w / 2;
      const centerY = this.rect.y + this.rect.h / 2;
      let angle = Math.atan2(cy - centerY, cx - centerX);
      if (s.shiftKey) angle = snapTo15Deg(angle);
      this.rect = { x: this.rect.x, y: this.rect.y, w: this.rect.w, h: this.rect.h, rotation: angle };
      return;
    }

    if (d.handle === null) {
      // New rect from anchor.
      let x0 = Math.min(d.anchorX, cx);
      let y0 = Math.min(d.anchorY, cy);
      let x1 = Math.max(d.anchorX, cx);
      let y1 = Math.max(d.anchorY, cy);
      if (this.aspectRatio) {
        const constrained = this.constrainFromAnchor(
          d.anchorX, d.anchorY, cx, cy, this.aspectRatio,
        );
        x0 = constrained.x0; y0 = constrained.y0;
        x1 = constrained.x1; y1 = constrained.y1;
      }
      this.rect = { x: x0, y: y0, w: x1 - x0, h: y1 - y0, rotation: 0 };
      return;
    }

    // Handle drag — adjust the rect based on which handle is being dragged.
    if (!this.rect) return;
    const ar = this.aspectRatio;
    let { startX: rx, startY: ry, startW: rw, startH: rh } = d;

    switch (d.handle) {
      case "corner-tl": {
        let nx = rx + dx;
        let ny = ry + dy;
        let nw = rw - dx;
        let nh = rh - dy;
        if (ar) {
          const c = this.constrainCorner(nx, ny, nx + nw, ny + nh, rx + rw, ry + rh, ar);
          nx = c.x0; ny = c.y0; nw = c.x1 - c.x0; nh = c.y1 - c.y0;
        }
        this.rect = { x: nx, y: ny, w: nw, h: nh, rotation: d.startRotation };
        break;
      }
      case "corner-tr": {
        let ny = ry + dy;
        let nx1 = rx + rw + dx;
        let nh = rh - dy;
        if (ar) {
          const c = this.constrainCorner(rx, ny, nx1, ny + nh, rx, ry + rh, ar);
          nx1 = c.x1; ny = c.y0; nh = c.y1 - c.y0;
        }
        this.rect = { x: rx, y: ny, w: nx1 - rx, h: nh, rotation: d.startRotation };
        break;
      }
      case "corner-bl": {
        let nx = rx + dx;
        let ny1 = ry + rh + dy;
        let nw = rw - dx;
        if (ar) {
          const c = this.constrainCorner(nx, ry, nx + nw, ny1, rx + rw, ry, ar);
          nx = c.x0; ny1 = c.y1; nw = c.x1 - c.x0;
        }
        this.rect = { x: nx, y: ry, w: nw, h: ny1 - ry, rotation: d.startRotation };
        break;
      }
      case "corner-br": {
        let nx1 = rx + rw + dx;
        let ny1 = ry + rh + dy;
        if (ar) {
          const c = this.constrainCorner(rx, ry, nx1, ny1, rx, ry, ar);
          nx1 = c.x1; ny1 = c.y1;
        }
        this.rect = { x: rx, y: ry, w: nx1 - rx, h: ny1 - ry, rotation: d.startRotation };
        break;
      }
      case "edge-t": {
        let ny = ry + dy;
        let nh = rh - dy;
        if (ar) {
          const targetW = rw;
          nh = Math.max(1, Math.round(targetW / ar));
          ny = ry + rh - nh;
        }
        this.rect = { x: rx, y: ny, w: rw, h: nh, rotation: d.startRotation };
        break;
      }
      case "edge-b": {
        let ny1 = ry + rh + dy;
        let nh = ny1 - ry;
        if (ar) {
          nh = Math.max(1, Math.round(rw / ar));
          ny1 = ry + nh;
        }
        this.rect = { x: rx, y: ry, w: rw, h: nh, rotation: d.startRotation };
        break;
      }
      case "edge-l": {
        let nx = rx + dx;
        let nw = rw - dx;
        if (ar) {
          const targetH = rh;
          nw = Math.max(1, Math.round(targetH * ar));
          nx = rx + rw - nw;
        }
        this.rect = { x: nx, y: ry, w: nw, h: rh, rotation: d.startRotation };
        break;
      }
      case "edge-r": {
        let nx1 = rx + rw + dx;
        let nw = nx1 - rx;
        if (ar) {
          nw = Math.max(1, Math.round(rh * ar));
          nx1 = rx + nw;
        }
        this.rect = { x: rx, y: ry, w: nw, h: rh, rotation: d.startRotation };
        break;
      }
    }
  }

  onPointerUp(ctx: ToolContext, _s: PointerSample): void {
    if (this.straightenPhase === "drawing") {
      this.straightenPhase = "idle";
      // Compute the rotation angle from the straighten line and apply it.
      const dx = this.straightenEndX - this.straightenStartX;
      const dy = this.straightenEndY - this.straightenStartY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > 2 && this.rect) {
        // The angle of the line relative to horizontal.
        const angle = Math.atan2(dy, dx);
        // Rotate the crop rect so the drawn line becomes horizontal.
        this.rect = { x: this.rect.x, y: this.rect.y, w: this.rect.w, h: this.rect.h, rotation: -angle };
      }
      this.isStraightenActive = false;
      ctx.requestRender();
      return;
    }
    this.drag = null;
    // Redraw so the finalized crop rect (with handles ready for adjustment)
    // stays painted after the pointer is released.
    ctx.requestRender();
  }

  getCurrentRect(): CropRect | null {
    return this.rect;
  }

  getRotation(): number {
    return this.rect?.rotation ?? 0;
  }

  reset(doc: Document): void {
    if (!doc) return;
    this.drag = null;
    this.rect = { x: 0, y: 0, w: doc.width, h: doc.height, rotation: 0 };
  }

  startStraighten(): void {
    this.isStraightenActive = true;
    this.straightenPhase = "idle";
  }

  getStraightenLine(): { x1: number; y1: number; x2: number; y2: number } | null {
    if (this.straightenPhase !== "drawing") return null;
    return {
      x1: this.straightenStartX,
      y1: this.straightenStartY,
      x2: this.straightenEndX,
      y2: this.straightenEndY,
    };
  }

  cancelStraighten(): void {
    this.straightenPhase = "idle";
    this.isStraightenActive = false;
  }

  /**
   * Called when the host dispatches a dblclick event. Commits the crop if
   * the click is inside the current crop rect; ignores otherwise (#221).
   */
  onDoubleClick(ctx: ToolContext, x: number, y: number): void {
    if (!this.rect || this.rect.w <= 0 || this.rect.h <= 0) return;
    // Convert screen coords via viewport (ToolContext carries the viewport).
    const p = screenToDoc(ctx.viewport, x, y);
    const px = Math.floor(p.x);
    const py = Math.floor(p.y);
    if (this.isInsideRect(this.rect, px, py)) {
      this.commit(ctx);
    }
  }

  commit(ctx: ToolContext): void {
    if (!ctx.doc) return;
    const r = this.rect;
    if (!r || r.w <= 0 || r.h <= 0) return;
    this.drag = null;
    this.rect = null;
    ctx.emit(new CropCommand(r.w, r.h, -r.x, -r.y, this.deletePixels, r.rotation, this.contentAware));
  }

  cancel(ctx: ToolContext): void {
    this.drag = null;
    this.rect = null;
    this.straightenPhase = "idle";
    this.isStraightenActive = false;
    // Redraw so the crop marquee/shield actually disappears on Escape/cancel.
    ctx.requestRender();
  }

  // --- Internals -----------------------------------------------------------

  private hitTestHandle(rect: CropRect, px: number, py: number): CropHandle {
    const { x, y, w, h } = rect;
    const nearL = Math.abs(px - x) <= HANDLE_TOL;
    const nearR = Math.abs(px - (x + w)) <= HANDLE_TOL;
    const nearT = Math.abs(py - y) <= HANDLE_TOL;
    const nearB = Math.abs(py - (y + h)) <= HANDLE_TOL;

    if (nearL && nearT) return "corner-tl";
    if (nearR && nearT) return "corner-tr";
    if (nearL && nearB) return "corner-bl";
    if (nearR && nearB) return "corner-br";
    if (nearT && px > x && px < x + w) return "edge-t";
    if (nearB && px > x && px < x + w) return "edge-b";
    if (nearL && py > y && py < y + h) return "edge-l";
    if (nearR && py > y && py < y + h) return "edge-r";

    // Rotation handle: a small circle above the top-center edge.
    const rotHandleX = x + w / 2;
    const rotHandleY = y - ROTATION_HANDLE_DIST;
    const rotDx = px - rotHandleX;
    const rotDy = py - rotHandleY;
    if (Math.sqrt(rotDx * rotDx + rotDy * rotDy) <= HANDLE_TOL + 2) {
      return "rotation";
    }

    return null;
  }

  private isInsideRect(rect: CropRect, px: number, py: number): boolean {
    return px > rect.x && px < rect.x + rect.w && py > rect.y && py < rect.y + rect.h;
  }

  private constrainFromAnchor(
    ax: number, ay: number, cx: number, cy: number, ar: number,
  ): { x0: number; y0: number; x1: number; y1: number } {
    let w = Math.abs(cx - ax);
    let h = Math.round(w / ar);
    // Determine quadrant from cursor position relative to anchor.
    const signX = cx >= ax ? 1 : -1;
    const signY = cy >= ay ? 1 : -1;
    const x0 = signX >= 0 ? ax : ax - w;
    const y0 = signY >= 0 ? ay : ay - h;
    const x1 = signX >= 0 ? ax + w : ax;
    const y1 = signY >= 0 ? ay + h : ay;
    return { x0, y0, x1, y1 };
  }

  private constrainCorner(
    x0: number, y0: number, x1: number, y1: number,
    fixedX: number, fixedY: number, ar: number,
  ): { x0: number; y0: number; x1: number; y1: number } {
    // The corner opposite to the dragged one is at (fixedX, fixedY).
    // Constrain the dragged corner so that w/h = ar.
    const dw = Math.abs(x1 - fixedX);
    const dh = Math.abs(y1 - fixedY);
    const targetH = Math.round(dw / ar);
    const signY = y1 >= fixedY ? 1 : -1;
    const signX = x1 >= fixedX ? 1 : -1;
    const ny1 = fixedY + signY * targetH;
    const nx1 = fixedX + signX * dw;
    return { x0: Math.min(fixedX, nx1), y0: Math.min(fixedY, ny1), x1: Math.max(fixedX, nx1), y1: Math.max(fixedY, ny1) };
  }
}
